import { IonLoading } from '@ionic/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProvisionedMachine,
  useGetDirectConnectSyncDataLazyQuery,
} from '../../../api/graphql/generated';
import { GateFailed } from '../../../direct-connect/gate/gate-failed';
import {
  createDirectConnectMachineFromCloudMachine,
  createDirectConnectProvisionTaskFromClaim,
  createDirectConnectSynchronizeTaskFromSyncResponse,
  useDirectConnectSaveTasksAndNavigateToMachine,
} from '../../../direct-connect/helper/direct-connect-helper';
import { logger } from '../../../util/logger';
import { RegisterMachineProps } from './register-machine';
import { RegisterMachineDirectConnectQRCode } from './register-machine-direct-connect-qr-code';

const RegisterMachineDirectConnect = (props: RegisterMachineProps) => {
  const { onComplete } = props;

  const { i18n } = useTranslation();

  const [provisioned, setProvisioned] = useState<
    ProvisionedMachine | undefined
  >();

  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const [getDataToSync] = useGetDirectConnectSyncDataLazyQuery();
  const saveTasksAndNavigateToMachine =
    useDirectConnectSaveTasksAndNavigateToMachine();

  const finalizeProvisioning = useCallback(
    async (provisioned: ProvisionedMachine) => {
      setProvisioned(provisioned);
      setIsRegistrationComplete(true);

      const { id: machineId, teamId, clientId } = provisioned.machine;
      const serialNumber = provisioned.machine.state?.runtimeInfo.serialNumber;

      logger.info('finalizing direct connect provisioning', {
        machineId,
        serialNumber,
      });

      if (!provisioned.claim) {
        logger.error('unable to complete provisioning. claim not found', {
          machineId,
        });
        setError(i18n.t('direct_connect_provisioning_general_error'));
        return;
      }

      const dataToSyncResponse = await getDataToSync({
        variables: {
          input: {
            machineId,
            teamId,
          },
        },
        fetchPolicy: 'no-cache',
      });

      logger.info('getting sync data for direct connect', {
        machineId,
        serialNumber,
      });

      const dataToSync = dataToSyncResponse.data?.getDirectConnectSyncData;

      if (!dataToSync) {
        logger.error('error getting sync data for machine', {
          machineId,
        });
        setError(i18n.t('direct_connect_provisioning_general_error'));
        return;
      }

      logger.info('creating tasks for direct connect machine', {
        serialNumber,
        machineId,
      });

      const provisionTask = createDirectConnectProvisionTaskFromClaim(
        serialNumber,
        {
          ...provisioned.claim,
          clientId,
        }
      );

      const synchronizeTask =
        createDirectConnectSynchronizeTaskFromSyncResponse(
          serialNumber,
          dataToSync
        );

      const directConnectMachine = createDirectConnectMachineFromCloudMachine(
        provisioned.machine
      );

      await saveTasksAndNavigateToMachine(directConnectMachine, [
        provisionTask,
        synchronizeTask,
      ]);

      if (typeof onComplete === 'function') {
        onComplete(provisioned.machine);
      }
    },
    [i18n, getDataToSync, saveTasksAndNavigateToMachine, onComplete]
  );

  const retry = useCallback(async () => {
    if (provisioned) {
      finalizeProvisioning(provisioned);
    }
  }, [finalizeProvisioning, provisioned]);

  const showLoading = isRegistrationComplete && !error;

  return (
    <>
      <IonLoading isOpen={showLoading} message={i18n.t('common:please_wait')} />
      <RegisterMachineDirectConnectQRCode
        onRegistration={finalizeProvisioning}
        {...props}
      />
      <GateFailed
        isOpen={error !== undefined}
        title={i18n.t('common:direct_connect')}
        description={error ?? ''}
        retry={retry}
      />
    </>
  );
};

export { RegisterMachineDirectConnect };
